import React, { useRef } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { ComplianceRulePreviewCard } from '@features/rules/components/ComplianceRulePreviewCard';
import type { ComplianceRuleDocument, ProviderRuleResponse } from '@features/rules/types';
import { Box, Fade, Popover } from '@mui/material';
import { useModal } from '@store/hooks/useModal';
import { AnimatePresence, motion } from 'motion/react';

import type { ComplianceRule } from '../types';

interface ComplianceRulePopoverProps {
  rule: ComplianceRule | null;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const ComplianceRulePopover: React.FC<ComplianceRulePopoverProps> = ({
  rule,
  anchorEl,
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { open: openModal } = useModal();

  const handlePopoverEnter = () => {
    // Notify parent to cancel its close timeout
    if (onMouseEnter) {
      onMouseEnter();
    }
  };

  const handlePopoverLeave = () => {
    // Notify parent to start close timeout
    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  const handleOpenModal = () => {
    if (!rule) return;

    // Convert ComplianceRule to ProviderRuleResponse format for the modal
    const complianceRuleDoc: ComplianceRuleDocument = {
      rule_id: rule.id,
      body_md: rule.body_md,
      selector_text: rule.selector_text,
      title: rule.title || null,
      always_select: rule.always_select || false,
      state: rule.state || 'active',
      facets: rule.facets,
      source: rule.source,
    };

    const providerRuleResponse: ProviderRuleResponse = {
      rule: complianceRuleDoc,
      rule_id: rule.id,
      rule_type: 'compliance',
      is_global: false, // JotCoach rules are typically provider rules
      is_disabled: false,
      organization_id: null,
    };

    openModal(MODAL_TYPES.RULE_DETAIL, {
      rule: providerRuleResponse,
      isAdmin: false,
    });
  };

  if (!rule) {
    return null;
  }

  // Convert ComplianceRule to ComplianceRuleDocument for preview card
  const complianceRuleDoc: ComplianceRuleDocument = {
    rule_id: rule.id,
    body_md: rule.body_md,
    selector_text: rule.selector_text,
    title: rule.title || null,
    always_select: rule.always_select || false,
    state: rule.state || 'active',
    facets: rule.facets,
    source: rule.source,
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transitionDuration={200}
      // Prevent scroll lock/restoration that causes scroll jumps when popover closes
      disableScrollLock
      disableRestoreFocus
      disableAutoFocus
      sx={{
        pointerEvents: 'none',
        '& .MuiPopover-paper': {
          pointerEvents: 'auto',
          maxWidth: 300,
          width: 300,
          mt: 0.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'borderColors.primary',
          boxShadow: 3,
        },
      }} slots={{ transition: Fade }}
    >
      <Box
        ref={popoverRef}
        onMouseEnter={handlePopoverEnter}
        onMouseLeave={handlePopoverLeave}
        sx={{ p: 0 }}
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              <ComplianceRulePreviewCard
                rule={complianceRuleDoc}
                isGlobal={false}
                isDisabled={false}
                onClick={handleOpenModal}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Popover>
  );
};
